import Footer from '../home/components/Footer';
import PageSEO from '../../components/feature/PageSEO';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://arvakon.com';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Arvakon – Independent Technical Verification Authority',
  url: `${siteUrl}/about`,
  description: 'Arvakon operates as an independent technical verification authority providing structured third-party surveillance and compliance oversight within industrial manufacturing supply chains.',
  mainEntity: {
    '@type': 'Organization',
    name: 'Arvakon',
    url: siteUrl,
    legalName: 'CHAIN Endüstriyel Makine İnşaat Sanayi ve Ticaret Limited Şirketi',
    description: 'Independent technical verification services for industrial manufacturing supply chains.',
    areaServed: 'International',
  },
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="About Arvakon – Independent Technical Verification | Governance & Structure"
        description="Arvakon operates as an independent technical verification provider for industrial manufacturing supply chains. Learn about our governance framework, operational independence, and structured authority boundaries."
        keywords="about Arvakon, technical verification provider, independent inspection, governance framework, operational independence, manufacturing oversight"
        canonical={`${siteUrl}/about`}
        jsonLd={aboutJsonLd}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className="text-dark-primary text-xl font-semibold tracking-tight">
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Home</a>
            <a href="/services" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Services</a>
            <a href="/standards" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Standards</a>
            <a href="/projects" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Industry Applications</a>
            <a href="/about" className="text-sm text-dark-primary font-medium">About</a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">Contact</a>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <div className="pt-32 pb-10 px-8 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
            Corporate Profile
          </p>
          <h1 className="font-serif text-4xl text-dark-primary tracking-tight">
            About Arvakon
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-24 px-8">

        {/* Section 1 – Corporate Position */}
        <section className="px-8 pb-20 pt-10 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-16">
              <div className="w-48 shrink-0 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Corporate Position
                </h2>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-16">
                <div className="space-y-5 text-slate-600 text-[15px] leading-[1.85]">
                  <p>
                    Arvakon operates as an independent technical verification authority providing structured third-party surveillance and compliance oversight within industrial manufacturing supply chains.
                  </p>
                  <p>
                    Services are performed on behalf of equipment purchasers, EPC contractors, and project stakeholders requiring objective visibility into fabrication processes, inspection activities, and documentation compliance across multi-tier supplier environments.
                  </p>
                  <p>
                    Arvakon functions exclusively within the scope of technical verification and structured reporting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="border-t border-slate-100" />

        {/* Section 2 – Scope of Activities */}
        <section className="px-8 py-20 bg-[#F8FAFC]">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-16">
              <div className="w-48 shrink-0 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Scope of Activities
                </h2>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-16">
                <div className="space-y-5 text-slate-600 text-[15px] leading-[1.85]">
                  <p>Core activities include:</p>
                  <ul className="space-y-3 mt-6">
                    {[
                      'Manufacturing surveillance at fabrication facilities',
                      'Welding procedure and qualification review',
                      'Non-destructive testing witness and documentation review',
                      'Material certification and traceability verification',
                      'Protective coating inspection in accordance with project specifications',
                      'Supplier and sub-tier oversight within complex supply chains',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6">
                    Activities are performed against applicable international codes, standards, and client-defined inspection and test plans.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="border-t border-slate-100" />

        {/* Section 3 – Operating Principles */}
        <section className="px-8 py-20 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-16">
              <div className="w-48 shrink-0 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Operating Principles
                </h2>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-16">
                <div className="space-y-5 text-slate-600 text-[15px] leading-[1.85]">
                  <p>
                    Arvakon maintains strict operational independence from manufacturing entities subject to verification.
                  </p>
                  <p>Services are structured to ensure:</p>
                  <ul className="space-y-3 mt-6">
                    {[
                      'Impartial observation',
                      'Documented technical reporting',
                      'Alignment with contractual and regulatory frameworks',
                      'Clear scope definition and authority boundaries',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6">
                    Arvakon does not perform design approval, regulatory certification, notified body functions, or final acceptance authority. Responsibility for regulatory compliance and equipment acceptance remains with the equipment purchaser or designated authority.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="border-t border-slate-100" />

        {/* Section 4 – Governance & Legal Structure */}
        <section className="px-8 py-20 bg-[#F8FAFC]">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-16">
              <div className="w-48 shrink-0 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Governance &amp; Legal Structure
                </h2>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-16">
                <div className="space-y-5 text-slate-600 text-[15px] leading-[1.85]">
                  <p>
                    Arvakon operates as an independent technical entity.
                  </p>
                  <p>
                    Commercial invoicing and contractual execution are conducted through the registered legal entity:
                  </p>
                  <div className="py-5 px-6 bg-white border border-slate-200 rounded-md inline-block mt-4">
                    <p className="font-serif text-dark-primary text-[16px] tracking-tight">
                      CHAIN Endüstriyel Makine İnşaat Sanayi ve Ticaret Limited Şirketi
                    </p>
                    <div className="mt-3 space-y-1 text-slate-400 text-[13px] leading-relaxed">
                      <p>Adalet Mahallesi Manas Bulvarı No: 47/B 2809</p>
                      <p>Bayraklı, İzmir, Türkiye</p>
                      <p>Chamber Registration No: 1847981</p>
                      <p>Trade Registry No: 198673</p>
                      <p>Tax Office: Karşıyaka</p>
                      <p>Tax ID: 2090751868</p>
                      <p>Established: 29 September 2017</p>
                    </div>
                  </div>
                  <p className="mt-4">
                    This legal structure provides the administrative and commercial framework for Arvakon's technical activities.
                  </p>
                  <p className="mt-4">
                    ARVAKON operates under CHAIN Endüstriyel Makine İnşaat Sanayi ve Ticaret Limited Şirketi, an engineering and industrial project company established in 2017 in İzmir, Türkiye. The company does not conduct manufacturing operations and operates as an independent engineering and technical services provider.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="border-t border-slate-100" />

        {/* Section 5 – Governance & Independence Framework */}
        <section className="px-8 py-20 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-16">
              <div className="w-48 shrink-0 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Governance &amp; Independence Framework
                </h2>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-16">
                <div className="space-y-5 text-slate-600 text-[15px] leading-[1.85]">
                  <p>
                    Arvakon operates under a structured governance model designed to ensure technical impartiality and operational independence.
                  </p>
                  <p>
                    Verification activities are performed without financial interest in manufacturing outcomes, material procurement, or production performance metrics.
                  </p>
                  <p>The governance framework is based on the following principles:</p>
                  <ul className="space-y-3 mt-6">
                    {[
                      'Organizational separation from manufacturing entities subject to verification',
                      'No ownership interest in fabrication facilities',
                      'No commission-based compensation linked to production output',
                      'Clear contractual definition of scope and authority boundaries',
                      'Transparent documentation and reporting practices',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6">
                    Arvakon's role is limited to structured technical observation and reporting within the defined project scope.
                  </p>
                  <p>
                    Final regulatory approval, certification authority, and equipment acceptance responsibility remain with the designated client or regulatory body.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}